import { createContext, useRef, useContext, useCallback } from 'react'

import { useParams } from 'react-router-dom'

import { AuthContext } from '../../../context/AuthContext'
import { SessionContext } from '../../../context/SessionContext'
import { getGuiSessionConfig } from '../config/guiSessionConfig'

export const CodeContext = createContext()

// Small, non-cryptographic hash (same fnv1a scheme Workspace.jsx uses for
// render code-versioning). Folding it into the localStorage backup key means
// an instructor edit to the starter code - which changes the hash - simply
// stops matching old backups instead of needing to overwrite or delete them.
// A student's in-progress work under a previous version of the exercise is
// never destroyed, it just stops being picked up once the exercise changes.
function hashString(str) {
  let hash = 2166136261
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(16)
}

export const CodeProvider = ({ children }) => {
  const { id: sessionId } = useParams()
  const { currentUser } = useContext(AuthContext)
  const { session } = useContext(SessionContext)

  const codeRef = useRef(null)
  const backupKeyRef = useRef(null)

  // Initialize code from local storage on mount
  if (codeRef.current === null) {
      if (sessionId && currentUser?.id) {
          // Pass the full session object, not just its id - getGuiSessionConfig
          // only reads an instructor's saved starter code (session.test_code.
          // template) off an object, not a bare id. WorkspacePage gates mounting
          // CodeProvider until session.id matches the route, so session is
          // guaranteed loaded here.
          const guiConfig = getGuiSessionConfig(session || sessionId)
          // Non-GUI (Python family) sessions start from the instructor's template, the same
          // code /session's VizmentalIDE seeds its editor with.
          const sessionTemplate = session?.test_code?.template
          const defaultTemplate = guiConfig
              ? guiConfig.starterCode || ""
              : (typeof sessionTemplate === 'string' ? sessionTemplate : "")
          const legacyKey = `code_backup_${sessionId}_${currentUser.id}`
          const key = `${legacyKey}_${hashString(defaultTemplate)}`
          backupKeyRef.current = key
          let saved = localStorage.getItem(key)
          // Non-GUI backups used to be saved under the unhashed key. Move one over the first
          // time, so a student's in-progress code survives this change; moving (not copying)
          // means a later template edit is not hidden by that old backup again.
          if (saved === null && !guiConfig) {
              const legacySaved = localStorage.getItem(legacyKey)
              if (legacySaved !== null) {
                  localStorage.setItem(key, legacySaved)
                  localStorage.removeItem(legacyKey)
                  saved = legacySaved
              }
          }
          codeRef.current = saved || defaultTemplate
      } else {
          codeRef.current = ""
      }
  }

  const setCode = useCallback((newCode) => {
    codeRef.current = newCode

    // Update LocalStorage (Persist data)
    if (sessionId && currentUser?.id && backupKeyRef.current) {
       localStorage.setItem(backupKeyRef.current, newCode)
    }
  }, [sessionId, currentUser])

  return (
    <CodeContext.Provider value={{ codeRef, setCode }}>
      {children}
    </CodeContext.Provider>
  )
}
